import { useQuery } from '@tanstack/react-query';

import { type OpcaoPesquisavel } from '@/ui/components/select-pesquisavel';
import {
  type ClassificacaoItem,
  type ClassificacaoPersonalizada,
  listaClassificacoesPersonalizadasSchema,
} from '@fiscoai/contracts';

import { OPCOES_CLASSIFICACAO } from './classificacao';
import { request } from './http';

/**
 * Classificações personalizadas da empresa (spec 0009) no seletor de classificação. Comum a
 * documentos (item e nota inteira) e empresas (destino da regra): por isso mora em `lib/`.
 */

/** Chave da lista da empresa: a aba Classificações invalida a mesma ao gravar. */
export const chaveClassificacoesPersonalizadas = (empresaId: string) => [
  'classificacoes-personalizadas',
  empresaId,
];

export const rotaClassificacoesPersonalizadas = (empresaId: string) =>
  `/empresas/${empresaId}/classificacoes`;

export function useClassificacoesPersonalizadas(empresaId: string, habilitada = true) {
  return useQuery({
    queryKey: chaveClassificacoesPersonalizadas(empresaId),
    queryFn: ({ signal }) =>
      request(rotaClassificacoesPersonalizadas(empresaId), {
        schema: listaClassificacoesPersonalizadasSchema,
        signal,
      }),
    enabled: habilitada,
    staleTime: 30_000,
  });
}

export const GRUPO_PADRAO = 'Padrão do FiscoAI';
export const GRUPO_EMPRESA = 'Desta empresa';

const PREFIXO = 'personalizada:';

/** O valor da opção no seletor: a classificação do produto ou "personalizada:<id>". */
export function valorDaClassificacao(
  classificacao: ClassificacaoItem | null,
  personalizada: { id: string } | null,
): string | null {
  if (classificacao === 'PERSONALIZADA') return personalizada ? PREFIXO + personalizada.id : null;
  return classificacao;
}

/** O que foi escolhido, já no formato do contrato. */
export interface EscolhaClassificacao {
  classificacao: ClassificacaoItem;
  classificacaoPersonalizadaId: string | null;
}

export function escolhaDoValor(valor: string): EscolhaClassificacao {
  if (valor.startsWith(PREFIXO))
    return {
      classificacao: 'PERSONALIZADA',
      classificacaoPersonalizadaId: valor.slice(PREFIXO.length),
    };
  return { classificacao: valor as ClassificacaoItem, classificacaoPersonalizadaId: null };
}

type Resumo = Pick<ClassificacaoPersonalizada, 'id' | 'nome'>;

/**
 * As opções do seletor: as do produto e, num grupo "Desta empresa", as personalizadas ATIVAS.
 * A que já está escolhida entra mesmo desativada (para o valor continuar com o nome dela).
 */
export function opcoesDeClassificacao(
  personalizadas: readonly ClassificacaoPersonalizada[],
  atual: Resumo | null = null,
): OpcaoPesquisavel[] {
  const ativas: Resumo[] = personalizadas.filter((p) => p.ativa);
  const daEmpresa = atual && !ativas.some((p) => p.id === atual.id) ? [...ativas, atual] : ativas;
  if (daEmpresa.length === 0) return OPCOES_CLASSIFICACAO;
  return [
    ...OPCOES_CLASSIFICACAO.map((o) => ({ ...o, grupo: GRUPO_PADRAO })),
    ...daEmpresa.map((p) => ({
      valor: PREFIXO + p.id,
      // No próprio rótulo (nome acessível): o leitor de tela anuncia que está desativada.
      rotulo: personalizadas.some((x) => x.id === p.id && x.ativa)
        ? p.nome
        : `${p.nome} (desativada)`,
      grupo: GRUPO_EMPRESA,
    })),
  ];
}

/** Seletor de classificação de uma empresa: lista (com as dela) e conversões de valor. */
export function useOpcoesClassificacao(empresaId: string, atual: Resumo | null = null) {
  const consulta = useClassificacoesPersonalizadas(empresaId);
  return opcoesDeClassificacao(consulta.data?.items ?? [], atual);
}
